import { useCallback, useEffect, useRef, useState } from 'react'

export type CameraPermissionState =
	| 'checking'
	| 'granted'
	| 'prompt'
	| 'denied'
	| 'unsupported'

function readPermission(): Promise<CameraPermissionState> {
	if (!navigator.mediaDevices?.getUserMedia)
		return Promise.resolve('unsupported')
	if (!navigator.permissions?.query) return Promise.resolve('prompt')

	return navigator.permissions
		.query({ name: 'camera' as PermissionName })
		.then((status): CameraPermissionState => status.state)
		.catch((): CameraPermissionState => 'prompt') // Safari antigo não aceita 'camera': só dá para saber pedindo
}

/**
 * Estado da permissão de câmera, sempre reconferido (ao montar, ao voltar para o app e quando o
 * navegador avisa a mudança). No iOS/PWA o scanner nem sempre dispara o pedido sozinho, então
 * `request()` pede a câmera a partir de um toque do usuário.
 */
export function useCameraPermission() {
	const [state, setState] = useState<CameraPermissionState>('checking')
	const statusRef = useRef<PermissionStatus | null>(null)
	const streamRef = useRef<MediaStream | null>(null)

	// Solta a câmera que `request` manteve aberta
	const release = useCallback(() => {
		for (const track of streamRef.current?.getTracks() ?? []) track.stop()
		streamRef.current = null
	}, [])

	const refresh = useCallback(async () => {
		setState(await readPermission())
	}, [])

	useEffect(() => {
		let cancelled = false

		refresh()
		if (navigator.permissions?.query) {
			navigator.permissions
				.query({ name: 'camera' as PermissionName })
				.then((status) => {
					if (cancelled) return
					statusRef.current = status
					status.onchange = () => setState(status.state)
				})
				.catch(() => undefined)
		}

		const onVisible = () => {
			if (document.visibilityState === 'visible') refresh()
		}
		document.addEventListener('visibilitychange', onVisible)
		window.addEventListener('focus', refresh)

		return () => {
			cancelled = true
			release()
			if (statusRef.current) statusRef.current.onchange = null
			document.removeEventListener('visibilitychange', onVisible)
			window.removeEventListener('focus', refresh)
		}
	}, [refresh, release])

	// Abre a câmera a partir do toque do usuário e a MANTÉM aberta: no iOS (PWA) cada getUserMedia novo
	// pode pedir permissão de novo, então o scanner, que abre a câmera logo em seguida, aproveita esta
	// permissão ainda ativa. Quem usa deve chamar `release` quando o scanner fechar.
	const request = useCallback(async (): Promise<{
		granted: boolean
		error?: unknown
	}> => {
		if (!navigator.mediaDevices?.getUserMedia) {
			setState('unsupported')
			return { granted: false }
		}
		try {
			release()
			streamRef.current = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: 'environment' },
			})
			setState('granted')
			return { granted: true }
		} catch (error) {
			const name = (error as { name?: string })?.name
			if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
				setState('denied')
			} else {
				await refresh()
			}
			return { granted: false, error }
		}
	}, [refresh, release])

	return { state, request, refresh, release }
}

export type CameraPermission = ReturnType<typeof useCameraPermission>
